import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { pushApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  return !!(window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true);
}
function isMobileDevice() {
  return typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Custom pre-permission banner shown once per login/app-load on a mobile
 * device running as an installed PWA (standalone display mode) — matches the
 * "prompt on first login" requirement without burning the browser's one-shot
 * native permission dialog on an unexplained popup. Only calls the real
 * `Notification.requestPermission()` when the user taps "Allow" here; a
 * dismiss just hides the banner for this session so it can ask again next
 * login (browsers don't let JS re-prompt after an explicit "denied" though).
 */
export default function PushPermissionPrompt() {
  const { user } = useAuth();
  const authed = user && user !== false;
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  const subscribeToPush = async () => {
    const reg = await navigator.serviceWorker.ready;
    const vapidKey = await pushApi.vapidKey();
    if (!vapidKey) return;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
    await pushApi.subscribe({
      endpoint: sub.endpoint,
      keys: {
        p256dh: bufferToBase64Url(sub.getKey("p256dh")),
        auth: bufferToBase64Url(sub.getKey("auth")),
      },
    });
  };

  useEffect(() => {
    if (!authed) return;
    const supported = typeof Notification !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
    if (!supported || !isStandalonePwa() || !isMobileDevice()) return;
    if (Notification.permission !== "default") return; // already granted or browser-blocked
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [authed]);

  // Re-associate this device's push endpoint with whoever is logged in NOW,
  // every login — not just the first time permission is granted. Browsers
  // remember "granted" per-origin (not per-app-user), so on a device/browser
  // reused by a different account later, permission is already "granted" and
  // the banner above never re-shows; without this, the endpoint would stay
  // registered to the earlier user forever, misdelivering their notifications.
  useEffect(() => {
    if (!authed) return;
    const supported = typeof Notification !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
    if (!supported || Notification.permission !== "granted") return;
    subscribeToPush().catch(() => {});
  }, [authed, user?.id]);

  const handleAllow = async () => {
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      await pushApi.setPreference(permission === "granted" ? "granted" : "denied").catch(() => {});
      if (permission === "granted") await subscribeToPush();
    } catch {
      /* user can retry next login */
    } finally {
      setBusy(false);
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[200] flex justify-center px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
      data-testid="push-permission-prompt"
    >
      <div
        className="w-full max-w-sm rounded-2xl shadow-2xl p-4 flex items-start gap-3"
        style={{ backgroundColor: "#0B1E3F", border: "1px solid rgba(255,255,255,0.08)" }}
      >
        <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(59,130,246,0.18)" }}>
          <Bell className="h-5 w-5" color="#60A5FA" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-bold text-white">Stay in the loop</div>
          <p className="text-[12.5px] mt-0.5 leading-snug" style={{ color: "#94a3b8" }}>
            Turn on notifications to get new messages and job assignments the moment they happen.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleAllow}
              disabled={busy}
              data-testid="push-permission-allow-btn"
              className="h-9 px-4 rounded-lg text-[13px] font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: "#2563EB" }}
            >
              {busy ? "Enabling…" : "Allow notifications"}
            </button>
            <button
              onClick={() => setVisible(false)}
              data-testid="push-permission-dismiss-btn"
              className="h-9 px-3 rounded-lg text-[13px] font-semibold"
              style={{ color: "#94a3b8" }}
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={() => setVisible(false)}
          data-testid="push-permission-close-btn"
          aria-label="Close"
          className="h-6 w-6 rounded flex items-center justify-center shrink-0"
        >
          <X className="h-4 w-4" color="#64748b" />
        </button>
      </div>
    </div>
  );
}
